import { useLazyQuery } from '@apollo/client';
import { useStore } from 'context/store-context';
import { FILTER_CLAIMS, FILTER_ORDERS } from 'gql/queries';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Claim } from 'types/claim';
import { Order } from 'types/order';
import { format } from 'date-fns';
import { Text } from './Text';
import { SearchIcon } from '@heroicons/react/solid';

// Settings navigation items for search with comprehensive keywords
const settingsItems = [
  {
    name: 'Delivery Guarantee',
    to: '/settings/protection',
    keywords: [
      'guarantee',
      'protection',
      'shipping',
      'delivery',
      'coverage',
      'fees',
      'pricing',
      'percentage',
      'minimum',
      'maximum',
      'rules',
      'protection type',
      'default fee',
      'order value',
      'shipping protection',
      'delivery guarantee',
    ],
  },
  {
    name: 'Impact',
    to: '/settings/impact',
    keywords: [
      'impact',
      'tree',
      'gift',
      'donation',
      'planting',
      'eden',
      'veritree',
      'inspired',
      'environmental',
      'sustainability',
      'charity',
      'give back',
      'add-on',
      'tree cost',
      'free gifts',
      'co-branded',
      'thegoodapi',
    ],
  },
  {
    name: 'Resolution Portal',
    to: '/settings/resolution-portal/general',
    keywords: [
      'resolution',
      'portal',
      'customer',
      'support',
      'claim',
      'general',
      'settings',
      'customer portal',
      'claim resolution',
    ],
  },
  {
    name: 'Resolution Portal - Portal',
    to: '/settings/resolution-portal/portal',
    keywords: [
      'portal',
      'customer portal',
      'door',
      'access',
      'customer access',
      'claim portal',
      'resolution portal',
    ],
  },
  {
    name: 'Resolution Portal - Resolutions',
    to: '/settings/resolution-portal/resolutions',
    keywords: [
      'resolutions',
      'refund',
      'reorder',
      'replace',
      'resolution options',
      'claim resolutions',
      'resolution types',
    ],
  },
  {
    name: 'Resolution Portal - Media',
    to: '/settings/resolution-portal/media',
    keywords: [
      'media',
      'images',
      'videos',
      'photos',
      'upload',
      'claim media',
      'evidence',
      'documentation',
    ],
  },
  {
    name: 'Returns & Exchanges - Policy',
    to: '/settings/resolution-portal/returns/policy',
    keywords: ['return window', 'exchange window', 'eligibility', 'customer restrictions', 'product exclusions', 'shared policy'],
  },
  {
    name: 'Returns & Exchanges - Returns',
    to: '/settings/resolution-portal/returns/settings',
    keywords: ['return reasons', 'return evidence', 'return photos', 'enable returns'],
  },
  {
    name: 'Returns & Exchanges - Exchanges',
    to: '/settings/resolution-portal/returns/exchanges',
    keywords: ['variant exchange', 'size exchange', 'different product', 'replacement', 'enable exchanges'],
  },
  {
    name: 'Returns & Exchanges - Incentives',
    to: '/settings/resolution-portal/returns/incentives',
    keywords: ['incentives', 'keep original', 'keep item', 'store credit bonus', 'cheaper difference'],
  },
  {
    name: 'Returns & Exchanges - Shipping',
    to: '/settings/resolution-portal/returns/carriers',
    keywords: ['exchange shipping', 'return shipping', 'return label', 'carrier', 'warehouse', 'return location'],
  },
  {
    name: 'Returns & Exchanges - Review',
    to: '/settings/resolution-portal/returns/approval',
    keywords: ['exchange approval', 'return approval', 'manual review', 'inspection'],
  },
  {
    name: 'Returns & Exchanges - Notifications',
    to: '/settings/notifications?tab=returns',
    keywords: ['exchange emails', 'return emails', 'customer notifications', 'return reminder'],
  },
  {
    name: 'Order Editing - Customization',
    to: '/settings/order-settings/customization',
    keywords: [
      'order editing',
      'customization',
      'language',
      'translation',
      'wording',
      'button style',
      'appearance',
      'order editing language',
    ],
  },
  {
    name: 'Order Editing - Editing rules',
    to: '/settings/order-settings/rules',
    keywords: [
      'order',
      'edit',
      'address',
      'modify',
      'change',
      'update',
      'customer info',
      'shipping address',
      'order modification',
      'contact email',
      'order note',
      'delivery instructions',
      'quantity',
      'remove items',
      'variant swap',
      'add items',
      'editing deadline',
      'support',
      'cancellation',
      'cancel reason',
      'retention',
      'discount offer',
      'order editing support',
    ],
  },
  {
    name: 'Order Editing - Requests',
    to: '/order-editing/requests',
    keywords: [
      'requests',
      'approvals',
      'review proposal',
      'warehouse pause',
      'order editing requests',
    ],
  },
  {
    name: 'Order Editing - Usage & activity',
    to: '/order-editing/activity',
    keywords: [
      'usage',
      'activity',
      'analytics',
      'edit history',
      'edit billing',
      'settlement',
      'order editing usage',
    ],
  },
  {
    name: 'Notifications',
    to: '/settings/notifications',
    keywords: [
      'notification',
      'alert',
      'email',
      'sms',
      'text',
      'message',
      'communication',
      'preferences',
      'notify',
      'reminder',
      'updates',
      'alerts',
    ],
  },
  {
    name: 'Tracking',
    to: '/settings/tracking-templates',
    keywords: [
      'tracking',
      'shipment',
      'delivery',
      'carrier',
      'template',
      'tracking number',
      'shipping updates',
      'tracking email',
      'tracking template',
      'carrier tracking',
      'delivery status',
    ],
  },
  {
    name: 'Automations',
    to: '/settings/claim-automations',
    keywords: [
      'automation',
      'rule',
      'auto',
      'claim',
      'workflow',
      'automatic',
      'condition',
      'trigger',
      'action',
      'if then',
      'automated',
      'rules engine',
      'claim automation',
    ],
  },
  {
    name: 'Integrations',
    to: '/settings/integration',
    keywords: [
      'integration',
      'connect',
      'api',
      'webhook',
      'third-party',
      'app',
      'connection',
      'external',
      'sync',
      'connect app',
      'api key',
      'webhook url',
    ],
  },
  {
    name: 'Team',
    to: '/settings/team',
    keywords: [
      'team',
      'user',
      'member',
      'access',
      'permission',
      'role',
      'invite',
      'collaborator',
      'staff',
      'employee',
      'user management',
      'team member',
      'access control',
      'permissions',
    ],
  },
  {
    name: 'Billing',
    to: '/settings/billing/invoices',
    keywords: [
      'billing',
      'invoice',
      'payment',
      'subscription',
      'plan',
      'pricing',
      'charge',
      'cost',
      'fee',
      'invoice history',
      'payment method',
      'billing profile',
      'subscription plan',
    ],
  },
  {
    name: 'Billing Profile',
    to: '/settings/billing/profile',
    keywords: [
      'billing profile',
      'payment method',
      'credit card',
      'billing address',
      'payment info',
      'billing details',
    ],
  },
  {
    name: 'Account',
    to: '/settings/account',
    keywords: [
      'account',
      'profile',
      'user',
      'settings',
      'personal',
      'name',
      'email',
      'password',
      'change password',
      'user profile',
      'account settings',
      'personal info',
    ],
  },
];

const GeneralSearch = () => {
  const { storeId } = useStore();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [filterOrders, { loading: filterOrdersLoading }] = useLazyQuery(
    FILTER_ORDERS,
    {
      onCompleted: (data) => {
        console.log('Orders search results:', data?.orders);
        setOrdersResults(data?.orders || []);
      },
      onError(error) {
        console.error('Error searching orders:', error);
        setOrdersResults([]);
      },
    },
  );

  const [filterClaims, { loading: filterClaimsLoading }] = useLazyQuery(
    FILTER_CLAIMS,
    {
      onCompleted: (data) => {
        console.log('Claims search results:', data?.claims);
        setIssuesResults(data?.claims || []);
      },
      onError(error) {
        console.error('Error searching claims:', error);
        setIssuesResults([]);
      },
    },
  );

  const [ordersResult, setOrdersResults] = useState<Order[]>([]);
  const [issuesResult, setIssuesResults] = useState<Claim[]>([]);

  // Search settings based on query with improved matching
  const settingsResults = settingsItems.filter((item) => {
    const query = searchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/).filter((word) => word.length > 0);

    // Check if name matches
    const nameMatch = item.name.toLowerCase().includes(query);

    // Check if any keyword matches (both directions for better matching)
    const keywordMatch = item.keywords.some((keyword) => {
      const lowerKeyword = keyword.toLowerCase();
      // Check if query contains keyword or keyword contains query
      return (
        lowerKeyword.includes(query) ||
        query.includes(lowerKeyword) ||
        // Check if any word in query matches keyword
        queryWords.some(
          (word) => lowerKeyword.includes(word) || word.includes(lowerKeyword),
        )
      );
    });

    return nameMatch || keywordMatch;
  });

  useEffect(() => {
    if (searchQuery && searchQuery.length && storeId) {
      console.log('Searching with query:', searchQuery, 'storeId:', storeId);

      // Search orders
      filterOrders({
        variables: {
          filters: {
            _or: [
              { number: { _ilike: `%${searchQuery}%` } },
              { customerEmail: { _ilike: `%${searchQuery}%` } },
              { customerName: { _ilike: `%${searchQuery}%` } },
            ],
            storeId: { _eq: storeId },
          },
          limit: 100,
          offset: 0,
          order_by: [
            {
              createdAt: 'desc',
            },
          ],
        },
      }).catch((error) => {
        console.error('Error in filterOrders promise:', error);
      });

      // Search claims
      filterClaims({
        variables: {
          filters: {
            _or: [
              { number: { _ilike: `%${searchQuery}%` } },
              { order: { number: { _ilike: `%${searchQuery}%` } } },
              { order: { customerEmail: { _ilike: `%${searchQuery}%` } } },
              { order: { customerName: { _ilike: `%${searchQuery}%` } } },
            ],
            storeId: { _eq: storeId },
          },
          limit: 100,
          offset: 0,
          order_by: [
            {
              createdAt: 'desc',
            },
          ],
        },
      }).catch((error) => {
        console.error('Error in filterClaims promise:', error);
      });
    } else {
      setOrdersResults([]);
      setIssuesResults([]);
    }
  }, [searchQuery, filterClaims, filterOrders, storeId]);

  const clearResults = () => {
    setSearchQuery('');
    setOrdersResults([]);
    setIssuesResults([]);
    inputRef.current?.focus();
  };

  const cancelSearch = () => {
    clearResults();
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const hasResults =
    searchQuery &&
    (ordersResult.length > 0 ||
      issuesResult.length > 0 ||
      settingsResults.length > 0);

  const showCancel = isFocused || searchQuery.length > 0;

  return (
    <div className="relative flex min-w-0 w-full flex-col items-center justify-center">
      <div className="relative w-full flex items-center">
        {/* Search Icon */}
        <div className="absolute left-3 pointer-events-none">
          <SearchIcon className="h-5 w-5 text-gray-400" />
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          aria-label="Search orders, issues, and settings"
          className="h-9 w-full pl-10 pr-24 py-1.5 outline-none rounded-lg border border-gray-300 text-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
          onChange={({ target: { value } }) => setSearchQuery(value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Delay to allow click events on results
            setTimeout(() => setIsFocused(false), 200);
          }}
          placeholder="Search Orders, Issues, Settings..."
          value={searchQuery}
        />

        {/* Cancel Button */}
        {showCancel && (
          <button
            onClick={cancelSearch}
            className="absolute right-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
            type="button"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Loading State */}
      {(filterClaimsLoading || filterOrdersLoading) && searchQuery && (
        <div className="absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-full p-4 z-[70]">
          <div className="text-sm text-gray-600">Searching...</div>
        </div>
      )}

      {/* Results */}
      {hasResults && (
        <div className="max-h-[400px] overflow-y-auto absolute top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-[70]">
          {/* Settings Results */}
          {settingsResults.length > 0 && (
            <section className="mb-4">
              <header className="flex items-center justify-between border-b border-gray-200 pb-2 mb-2">
                <h3 className="text-gray-800 font-semibold text-sm uppercase tracking-wide">
                  Settings
                </h3>
                <span className="text-xs text-gray-500">
                  {settingsResults.length} found
                </span>
              </header>
              <ul className="divide-y divide-gray-100">
                {settingsResults.map((setting) => (
                  <li key={setting.to}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(setting.to);
                        cancelSearch();
                      }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <span className="text-sm font-medium">{setting.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Orders Results */}
          {ordersResult.length > 0 && (
            <section className="mb-4">
              <header className="flex items-center justify-between border-b border-gray-200 pb-2 mb-2">
                <h3 className="text-gray-800 font-semibold text-sm uppercase tracking-wide">
                  Orders
                </h3>
                <span className="text-xs text-gray-500">
                  {ordersResult.length} found
                </span>
              </header>
              <ul className="divide-y divide-gray-100">
                {ordersResult.map((order) => (
                  <li
                    key={order.id}
                    onClick={() => {
                      navigate(`/orders/${order.id}`);
                      cancelSearch();
                    }}
                    className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <span className="text-sm font-medium">
                      Order: {order.number}
                    </span>
                    <Text
                      value={`${format(
                        new Date(order.createdAt),
                        'do MMM yyyy hh:mm',
                      )}`}
                      type="sm"
                      className="text-gray-500"
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Issues Results */}
          {issuesResult.length > 0 && (
            <section>
              <header className="flex items-center justify-between border-b border-gray-200 pb-2 mb-2">
                <h3 className="text-gray-800 font-semibold text-sm uppercase tracking-wide">
                  Issues
                </h3>
                <span className="text-xs text-gray-500">
                  {issuesResult.length} found
                </span>
              </header>
              <ul className="divide-y divide-gray-100">
                {issuesResult.map((issue) => (
                  <li
                    key={issue.id}
                    onClick={() => {
                      navigate(`/claims/${issue.id}`);
                      cancelSearch();
                    }}
                    className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <span className="text-sm font-medium">
                      Issue: {issue.number}
                    </span>
                    <Text
                      value={`${format(
                        new Date(issue.createdAt),
                        'do MMM yyyy hh:mm',
                      )}`}
                      type="sm"
                      className="text-gray-500"
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {/* No Results */}
      {searchQuery &&
        !hasResults &&
        !filterClaimsLoading &&
        !filterOrdersLoading && (
          <div className="max-h-[400px] overflow-y-auto absolute top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-[70]">
            <div className="text-sm text-gray-600">No results found</div>
          </div>
        )}
    </div>
  );
};

export default GeneralSearch;
